import {
  COOKIE_BANNER_TOKENS,
  type CookieBannerTheme,
  type CookieBannerTokenKey,
} from "@loot-os/api-contract";
import { Button } from "@loot-os/ui/components/button";
import type { ReactNode } from "react";
import { useState } from "react";

import { ColorField, RadiusField } from "./StorefrontFields";

interface ThemeDraft {
  tokens: Partial<Record<CookieBannerTokenKey, string>>;
  radius: string;
}

/** The stored shape from a draft: blank fields are left out, so they keep following the store. */
function toTheme(draft: ThemeDraft): CookieBannerTheme {
  const tokens: Partial<Record<CookieBannerTokenKey, string>> = {};
  for (const token of COOKIE_BANNER_TOKENS) {
    const value = draft.tokens[token.key]?.trim();
    if (value) tokens[token.key] = value;
  }
  const radius = draft.radius.trim();
  return { ...(Object.keys(tokens).length ? { tokens } : {}), ...(radius ? { radius } : {}) };
}

function fromTheme(theme: CookieBannerTheme): ThemeDraft {
  return { tokens: { ...(theme.tokens ?? {}) }, radius: theme.radius ?? "" };
}

/**
 * The cookie banner's colors and corner radius, one field per token with the color it falls
 * back to as the placeholder. Shared by the platform default (`/legal`) and an
 * environment's override (Storefront → Legal / Consent); `fallbacks` is what "unset" shows,
 * which on a tenant's page is that store's own palette. Remount with `key` when the saved
 * value changes, like the other setting forms.
 */
export function CookieBannerThemeForm({
  theme,
  fallbacks,
  canEdit,
  saving,
  onSave,
  footer,
}: {
  theme: CookieBannerTheme;
  /** The color each token inherits while unset, keyed by token, as hex for the picker. */
  fallbacks: Record<CookieBannerTokenKey, string>;
  canEdit: boolean;
  saving: boolean;
  onSave: (theme: CookieBannerTheme) => void;
  footer?: ReactNode;
}) {
  const [draft, setDraft] = useState<ThemeDraft>(() => fromTheme(theme));
  const next = toTheme(draft);
  const dirty = JSON.stringify(next) !== JSON.stringify(toTheme(fromTheme(theme)));
  const setToken = (key: CookieBannerTokenKey, value: string) =>
    setDraft({ ...draft, tokens: { ...draft.tokens, [key]: value } });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(next);
  };

  return (
    <form className="grid gap-5" onSubmit={submit}>
      <CookieBannerPreview theme={next} fallbacks={fallbacks} />
      <div className="grid gap-4 sm:grid-cols-2">
        {COOKIE_BANNER_TOKENS.map((token) => (
          <ColorField
            key={token.key}
            id={`cookie-banner-${token.key}`}
            label={token.label}
            hint={`--cookie-banner-${token.key}`}
            fallback={fallbacks[token.key]}
            value={draft.tokens[token.key] ?? ""}
            onChange={(value) => setToken(token.key, value)}
          />
        ))}
      </div>
      <RadiusField value={draft.radius} onChange={(radius) => setDraft({ ...draft, radius })} />
      {canEdit && (
        <div className="flex flex-wrap items-center gap-2">
          <Button type="submit" disabled={saving || !dirty}>
            {saving ? "Saving…" : "Save banner style"}
          </Button>
          {footer}
        </div>
      )}
    </form>
  );
}

/**
 * A mock of the banner in the draft's colors, so a change is visible before it is saved.
 * Termly's real layout differs (its copy, its links, its button set); only the colors, the
 * radius and the page font are the storefront's to decide, and those are what this shows.
 */
export function CookieBannerPreview({
  theme,
  fallbacks,
}: {
  theme: CookieBannerTheme;
  fallbacks: Record<CookieBannerTokenKey, string>;
}) {
  const color = (key: CookieBannerTokenKey) => theme.tokens?.[key]?.trim() || fallbacks[key];
  const radius = theme.radius?.trim() || "10px";
  return (
    <div className="rounded-md border border-dashed border-border bg-muted/30 p-4">
      <p className="mb-3 text-xs text-muted-foreground">
        Preview (a mock; Termly's own layout differs)
      </p>
      <div
        className="flex flex-col gap-3 p-4 text-sm shadow-md sm:flex-row sm:items-center sm:justify-between"
        style={{
          background: color("background"),
          color: color("foreground"),
          border: `1px solid ${color("border")}`,
          borderRadius: radius,
        }}
      >
        <span>
          We use essential cookies to make our site work. With your consent, we may also use
          non-essential cookies. <span className="underline">Cookie Policy</span> ·{" "}
          <span className="underline">Preferences</span>
        </span>
        <span
          className="inline-flex shrink-0 items-center justify-center px-4 py-2 font-semibold"
          style={{
            background: color("button"),
            color: color("button-foreground"),
            borderRadius: radius,
          }}
        >
          Accept
        </span>
      </div>
    </div>
  );
}
